import { useEffect, useState } from 'react';
import { api } from '@/shared/api/client';
import { Drawer } from '@/shared/ui/Drawer';
import { useTenantScope } from '@/app/providers/TenantScopeProvider';

interface Tenant {
  Id: string;
  Name: string;
}
interface Company {
  Id: string;
  Name: string;
}

export function TenantSwitcher() {
  const scope = useTenantScope();
  const [open, setOpen] = useState(false);
  const [tenants, setTenants] = useState<Tenant[] | null>(null);
  const [companiesByTenant, setCompaniesByTenant] = useState<Record<string, Company[]>>({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || tenants !== null) return;
    setLoading(true);
    api
      .get<Tenant[]>('/api/tenant/my')
      .catch(() => [] as Tenant[])
      .then(async (ts) => {
        setTenants(ts);
        // Tenant бүрийн компаниудыг параллел татна.
        const pairs = await Promise.all(
          ts.map(async (t) => {
            const list = await api
              .get<Company[]>(`/api/company/by-tenant/${t.Id}`)
              .catch(() => [] as Company[]);
            return [t.Id, list] as const;
          }),
        );
        setCompaniesByTenant(Object.fromEntries(pairs));
      })
      .finally(() => setLoading(false));
  }, [open, tenants]);

  function pickTenant(t: Tenant) {
    if (t.Id === scope.tenantId) {
      setOpen(false);
      return;
    }
    // Зөвхөн tenant солих → companyId-ыг тенант доторх 1-р company-руу. Хэрэв
    // эхний компанийг олж чадахгүй бол dashboard-руу navigate хийгээгүй,
    // SelectScopePage-руу explicit redirect хийнэ.
    const cs = companiesByTenant[t.Id] ?? [];
    if (cs.length === 0) {
      // тенантад компани алга — pickCompany flow ашиглана.
      setOpen(false);
      return;
    }
    scope.switchTo({ tenantId: t.Id, companyId: cs[0]!.Id });
    setOpen(false);
  }

  function pickCompany(t: Tenant, c: Company) {
    if (t.Id === scope.tenantId && c.Id === scope.companyId) {
      setOpen(false);
      return;
    }
    scope.switchTo({ tenantId: t.Id, companyId: c.Id });
    setOpen(false);
  }

  if (!scope.tenantName && !scope.companyName) return null;

  return (
    <>
      <button
        type="button"
        className="ts-switcher-trigger"
        onClick={() => setOpen(true)}
        aria-expanded={open}
        title={`${scope.tenantName ?? '—'} › ${scope.companyName ?? ''}`}
      >
        <i className="bi bi-building ts-switcher-icon" />
        <span className="ts-switcher-text">
          <span className="ts-switcher-tenant">{scope.tenantName ?? '—'}</span>
          {scope.companyName ? (
            <span className="ts-switcher-company">
              <i className="bi bi-briefcase" /> {scope.companyName}
            </span>
          ) : null}
        </span>
        <i className="bi bi-chevron-down ts-switcher-chev" />
      </button>

      <Drawer
        open={open}
        onClose={() => setOpen(false)}
        width="420px"
        title={
          <span>
            <i className="bi bi-building mr-2" /> Тенант сонгох
          </span>
        }
      >
        {loading || tenants === null ? (
          <div className="ts-switcher-loading">
            <span className="spinner-border spinner-border-sm mr-2" /> Уншиж байна…
          </div>
        ) : tenants.length === 0 ? (
          <div className="ts-switcher-empty">Тенант алга</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            {tenants.map((t) => {
              const isActiveT = t.Id === scope.tenantId;
              const cs = companiesByTenant[t.Id] ?? [];
              return (
                <div key={t.Id} className={`ts-sw-group${isActiveT ? ' active' : ''}`}>
                  <button
                    type="button"
                    className={`ts-sw-tenant-row${isActiveT ? ' active' : ''}`}
                    onClick={() => pickTenant(t)}
                    title="Энэ тенантыг идэвхжүүлэх"
                  >
                    <i className="bi bi-building ts-switcher-item-icon" />
                    <span className="ts-switcher-item-label">{t.Name}</span>
                    {isActiveT ? (
                      <i className="bi bi-check-lg ts-switcher-check" />
                    ) : null}
                  </button>
                  <div className="ts-sw-company-list">
                    {cs.length === 0 ? (
                      <div className="ts-switcher-empty">Компани алга</div>
                    ) : (
                      cs.map((c) => {
                        const isActiveC = isActiveT && c.Id === scope.companyId;
                        return (
                          <button
                            key={c.Id}
                            type="button"
                            className={`ts-switcher-item${isActiveC ? ' active' : ''}`}
                            onClick={() => pickCompany(t, c)}
                          >
                            <i className="bi bi-briefcase ts-switcher-item-icon" />
                            <span className="ts-switcher-item-label">{c.Name}</span>
                            {isActiveC ? (
                              <i className="bi bi-check-lg ts-switcher-check" />
                            ) : null}
                          </button>
                        );
                      })
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Drawer>
    </>
  );
}
